import * as React from 'react';
import { useSelector } from 'react-redux';
import { Responsive } from '@cian/ui-kit';
import { BlackFridayListingLinkContainer } from './parts/BlackFridayListingLinkContainer';
import { trackLinkClick } from './tracking';
import { OffersLink } from '../../components/OffersLink';
import { useOffersListingUrl } from '../../hooks';
import { selectPromoDetails, selectPromoProduct } from '../../selectors/promoDetails';
import { IPromoProduct } from '../../types/product';
import { IPromoOffer } from '../../types/promoOffersSelection';
import { PromoOfferCardContext } from '../../utils/promoOfferCardContext';
import { CallButton } from '../CallButton';
import { MobileCallButton } from '../CallButton/MobileCallButton';
import { trackCallButtonClick } from '../CallButton/tracking';

export const Controls = () => {
  const promoDetails = useSelector(selectPromoDetails);
  const promoProduct = useSelector(selectPromoProduct);

  const isBlackFriday = promoDetails?.promoPopupType === 'coPromos';

  const offersListingUrl = useOffersListingUrl(promoDetails?.newbuildingIds || []);

  const handleNewbuildingListingLinkClick = React.useCallback(() => {
    trackLinkClick({ isBlackFriday, product: promoProduct as IPromoProduct, type: 'to_listingjk' });
  }, [isBlackFriday, promoProduct]);

  const handleOfferListingLinkClick = React.useCallback(() => {
    trackLinkClick({ isBlackFriday, product: promoProduct as IPromoProduct, type: 'to_offers' });
  }, [isBlackFriday, promoProduct]);

  const onCallClick = React.useCallback(() => {
    const id = promoDetails?.phoneNewbuildingId;
    const gaLabel = promoDetails?.gaLabel;
    const promoId = promoDetails?.uniqueId;

    if (!id || !gaLabel || !promoId) {
      return;
    }

    trackCallButtonClick({
      customUrl: gaLabel,
      id,
      action: 'Open_detail_promo',
      extra: { promoId },
    });
  }, [promoDetails]);

  const callButtonContext = React.useMemo(() => {
    const phone = promoDetails?.builderPhone;
    if (phone && phone.countryCode) {
      phone.countryCode = phone.countryCode.replace(/\D/g, '');
    }

    const builder = {
      phones: promoDetails?.builderPhone ? [promoDetails.builderPhone] : [],
    };

    return {
      builder,
      siteBlockId: promoDetails?.siteBlockId,
      offerId: promoDetails?.phoneNewbuildingId,
    } as IPromoOffer;
  }, [promoDetails]);

  if (isBlackFriday) {
    return (
      <BlackFridayListingLinkContainer
        newbuildingCount={promoDetails.newbuildingCount}
        newbuildingIds={promoDetails.newbuildingIds}
        onClick={handleNewbuildingListingLinkClick}
      />
    );
  }

  return (
    <>
      <OffersLink url={offersListingUrl} onClick={handleOfferListingLinkClick} />
      <PromoOfferCardContext.Provider value={callButtonContext}>
        <Responsive desktop tablet>
          <CallButton onClick={onCallClick} theme="fill_primary" size="M" placeType="promoPopup" />
        </Responsive>
        <Responsive phone>
          <MobileCallButton placeType="promoPopup" />
        </Responsive>
      </PromoOfferCardContext.Provider>
    </>
  );
};
